import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import type { SignUpPayload } from '@/api/v4/auth.api';
import { fetchWhoAmI, signUp } from '@/api/v4/auth.api';
import { BaseExceptions, isBaseError } from '@/api/v4/base-fetch';
import { QueryKey } from '@/config/query-client';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { notify } from '@/ui/snackbar/notify';
import { currentUserQuery } from '@/user/use-current-user.query';

import { jwtTokenState } from '../auth.state';
import { captureSignUpError } from '../helpers/capture-sign-up-error';

export const useSignUp = () => {
  const navigateTo = useNavigateTo();

  const { t } = useTranslation('auth');
  const queryClient = useQueryClient();
  const setToken = useSetAtom(jwtTokenState);

  const mutation = useMutation({
    mutationFn: async (payload: SignUpPayload) => await signUp(payload),
    onSuccess: async data => {
      setToken('Bearer ' + data.token);
      await queryClient.invalidateQueries({ queryKey: [QueryKey.WhoAmI] });

      try {
        const user = await fetchWhoAmI();
        queryClient.setQueryData(currentUserQuery().queryKey, user);
      } catch {
        // User fetch failed; navigation still proceeds
      }

      navigateTo.home();
    },
    onError: error => {
      captureSignUpError(error);

      if (isBaseError(error)) {
        if (error.response.status === BaseExceptions.NOT_FOUND) {
          notify({ message: t`signUpAccountAlreadySetUp` });
          navigateTo.signIn();
          return;
        }
        if (error.response.status === BaseExceptions.UNAUTHORIZED) {
          notify({ message: t`loginErrorNoUser` });
          return;
        }
      }
      notify({ message: t`loginError` });
    },
  });

  return {
    handleSignUp: mutation.mutate,
  };
};
